import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import {
	Icon,
	IconButton,
	ReorderableList,
	Typography
} from "client/Components";
import { GlobalNavigationItemKind } from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { deleteSpace, SpaceKind } from "client/Api/Workspace/workspace";
import styles from "./SpaceGlobalNavigationListItem.module.less";
import {
	GlobalNavigationGroupAndIndex,
	GlobalNavigationItemMenu
} from "./GlobalNavigationItemMenu";
import { GlobalNavigationItemContent } from "./GlobalNavigationItemContent";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { DeleteConfirmationDialogData } from "./DeleteConfirmationDialog";
import { SpaceGlobalNavigationItemFormModel } from "../misc";
import {
	AlertManager,
	AutoFocusState,
	useAlerts,
	useAutoFocus,
	useItemMenuState
} from "client/Hooks";
import { Guid } from "client_legacy/ndaw/guid";
import { getClientId } from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import {
	WorkspaceEvent,
	publish as workspacePublish
} from "client/Features/Workspace/events";

export type SpaceGlobalNavigationListItemProps = {
	index: number;
	itemHandler: IValueInputAttachable<SpaceGlobalNavigationItemFormModel>;
	autoFocusState: AutoFocusState<Guid>;
	onDelete: () => void;
	onEdit: (spaceId: number) => void;
	onOpenDeleteConfirmation: (data: DeleteConfirmationDialogData) => Promise<boolean>;
	groups: readonly GlobalNavigationGroupAndIndex[];
	containingGroupIndex: number | null;
	onMoveToGroup: (
		itemIndex: number,
		sourceGroupIndex: number | null,
		targetGroupIndex: number
	) => void;
	onMoveOutOfGroup: (
		itemIndex: number,
		sourceGroupIndex: number
	) => void;
	onChangeIsBusy: (isBusy: boolean) => void;
};

export const SpaceGlobalNavigationListItem = (props: SpaceGlobalNavigationListItemProps): ReactElement => {
	const {
		index,
		itemHandler,
		autoFocusState,
		onEdit,
		onDelete,
		onOpenDeleteConfirmation,
		containingGroupIndex,
		groups,
		onMoveOutOfGroup,
		onMoveToGroup,
		onChangeIsBusy
	} = props;

	const {
		spaceKind,
		userInfo,
		name,
		spaceData
	} = itemHandler.value;

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const moreButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const isSharedSpace: boolean = spaceKind === SpaceKind.Shared;

	const alertManager: AlertManager = useAlerts();
	const [autoFocus] = useAutoFocus<Guid>(getClientId(itemHandler.value), autoFocusState);

	useEffect(
		(): void => {
			if (autoFocus === "focus") {
				itemRef.current
					?.scrollIntoView();

				autoFocusState.changeTo(null);
			}
		},
		[autoFocus, autoFocusState]
	);

	const deleteHandler = useCallback(
		async (): Promise<void> => {
			if (!userInfo.canDelete) {
				return;
			}

			if (!await onOpenDeleteConfirmation({ name })) {
				return;
			}

			try {
				onChangeIsBusy(true);
				await deleteSpace(spaceData.spaceId);

				workspacePublish(
					WorkspaceEvent.SpaceDeleted,
					{
						spaceId: spaceData.spaceId
					}
				);

				onDelete();
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			} finally {
				onChangeIsBusy(false);
			}
		},
		[
			alertManager,
			name,
			onDelete,
			onChangeIsBusy,
			onOpenDeleteConfirmation,
			spaceData.spaceId,
			userInfo.canDelete,
		]
	);

	const moveToGroupHandler = useCallback(
		(targetGroupIndex: number): void => {
			onMoveToGroup(index, containingGroupIndex, targetGroupIndex);
		},
		[
			containingGroupIndex,
			index,
			onMoveToGroup
		]
	);

	const moveOutOfGroupHandler = useMemo(
		(): (() => void) | undefined => {
			if (isNil(containingGroupIndex)) {
				return undefined;
			}

			return (): void => {
				onMoveOutOfGroup(index, containingGroupIndex);
			};
		},
		[
			containingGroupIndex,
			index,
			onMoveOutOfGroup
		]
	);

	const hasActions: boolean = userInfo.canDelete || userInfo.canEdit;

	return (
		<>
			<ReorderableList.RowItem
				trackingId="space-list-item"
				actions={hasActions
					? (
						<IconButton
							ref={moreButtonRef}
							onClick={() => onOpenMoreMenu()}
							icon="ellipsis"
							kind="transparent"
							trackingId="more"
							shape="round"
							isActive={menuState.isOpen && !menuState.isContextMenu}
						/>
					)
					: undefined
				}
				isActive={menuState.isOpen}
				mouseInteraction={{
					onContextMenu: hasActions
						? onOpenContextMenu
						: undefined
				}}
			>
				<GlobalNavigationItemContent
					icon={
						<div className={styles.icon}>
							<Icon
								value={{
									kind: "font",
									name: isSharedSpace ? "objects-column" : "user",
									color: "primary"
								}}
							/>
						</div>
					}
				>
					<div
						{...setTrackingId("space-item-name")}
						ref={itemRef}
					>
						<Typography weight="bold">
							{name}
						</Typography>
					</div>
				</GlobalNavigationItemContent>
			</ReorderableList.RowItem>
			{hasActions && menuState.isOpen && (
				<GlobalNavigationItemMenu
					menuState={menuState}
					kind={GlobalNavigationItemKind.Space}
					target={ensureNotNil(moreButtonRef.current)}
					onClose={onCloseMenu}
					onDelete={
						userInfo.canDelete
							? deleteHandler
							: undefined
					}
					onEdit={
						userInfo.canEdit
							? () => onEdit(spaceData.spaceId)
							: undefined
					}
					groups={groups}
					onMoveOutOfGroup={moveOutOfGroupHandler}
					onMoveToGroup={moveToGroupHandler}
					containingGroupIndex={containingGroupIndex}
					menuTitle={name}
				/>
			)}
		</>
	);
};
SpaceGlobalNavigationListItem.displayName = "SpaceGlobalNavigationListItem";
